'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

interface PasswordStrengthMeterProps {
  password: string;
}

// Order matches the zxcvbn score 0–4; index into the translated labels below.
const LABEL_KEYS = ['veryWeak', 'weak', 'fair', 'good', 'strong'] as const;
const COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-green-500',
] as const;

/**
 * Lightweight password strength meter using zxcvbn-ts. zxcvbn returns a
 * score 0–4; we render a colored bar and a text label.
 *
 * Pure visual: this does NOT block form submission. The validator
 * (passwordSchema in user.schema.ts) handles the actual blocking rules.
 *
 * The zxcvbn dictionaries are loaded asynchronously the first time the
 * component is used (~150KB of language data). The component renders
 * a placeholder bar until the loader resolves.
 */
export function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps) {
  const t = useTranslations('auth');
  const [score, setScore] = useState<number>(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [core, langCommon, langEn] = await Promise.all([
          import('@zxcvbn-ts/core'),
          import('@zxcvbn-ts/language-common'),
          import('@zxcvbn-ts/language-en'),
        ]);
        if (cancelled) return;
        core.zxcvbnOptions.setOptions({
          translations: langEn.translations,
          graphs: langCommon.adjacencyGraphs,
          dictionary: {
            ...langCommon.dictionary,
            ...langEn.dictionary,
          },
        });
        setReady(true);
      } catch (err) {
        console.error('[password-strength] Failed to load zxcvbn', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || password.length === 0) {
      setScore(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const { zxcvbn } = await import('@zxcvbn-ts/core');
      const result = zxcvbn(password);
      if (!cancelled) setScore(result.score);
    })();
    return () => {
      cancelled = true;
    };
  }, [password, ready]);

  const visible = password.length > 0;
  const label = useMemo(
    () => t(`passwordStrength.${LABEL_KEYS[score] ?? LABEL_KEYS[0]}`),
    [score, t]
  );

  if (!visible) return null;

  return (
    <div className="space-y-1" aria-live="polite">
      <div className="flex h-1.5 w-full gap-1 rounded bg-gray-200">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-full flex-1 rounded transition-colors ${
              i <= score ? COLORS[score] : 'bg-transparent'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>
      <p className="text-xs text-gray-600">
        {t('passwordStrength.label')} <span className="font-medium">{label}</span>
      </p>
    </div>
  );
}
